import {useApps} from '@cohort/merchants/apps/useApps';
import AdminPage from '@cohort/merchants/components/AdminPage';
import Button from '@cohort/merchants/components/buttons/Button';
import EmptyState from '@cohort/merchants/components/EmptyState';
import ErrorState from '@cohort/merchants/components/ErrorState';
import KPICard from '@cohort/merchants/components/KPICard';
import LoadingState from '@cohort/merchants/components/LoadingState';
import {useConnections} from '@cohort/merchants/hooks/api/Connections';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import EditConnectionModal from '@cohort/merchants/pages/connections/new/EditConnectionModal';
import ConnectionsTable from '@cohort/merchants/pages/settings/apps/ConnectionsTable';
import type {AppId, MerchantConnectorId} from '@cohort/shared/schema/apps';
import {AppIdSchema} from '@cohort/shared/schema/apps';
import type {AppSpecWithMerchantConnector} from '@cohort/shared/schema/apps/app';
import {Link} from '@phosphor-icons/react';
import {Fragment, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {match} from 'ts-pattern';

const AppOverviewPage: React.FC = () => {
  const {t} = useTranslation('pages', {keyPrefix: 'settings.apps.appOverviewPage'});
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [appSpec, setAppSpec] = useState<AppSpecWithMerchantConnector | null>(null);
  const merchant = useCurrentMerchant();
  const navigate = useNavigate();
  const {appId} = useParams();
  const {getApp, merchantConnectionCreationDisabled} = useApps();
  const app = getApp(appId);

  const hideCreateConnectionButton =
    appSpec !== null && merchantConnectionCreationDisabled(appSpec.id as AppId);

  useEffect(() => {
    if (
      appId === undefined ||
      AppIdSchema.safeParse(appId).success === false ||
      app === null ||
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      app.spec.merchantConnector === null
    ) {
      navigate('/not-found', {replace: true});
      return;
    }
    setAppSpec(app.spec as unknown as AppSpecWithMerchantConnector);
  }, [app, appId, navigate]);

  const merchantConnectorId = appSpec
    ? (appSpec.merchantConnector.id as MerchantConnectorId)
    : undefined;

  const {
    data: merchantConnections,
    isLoading: isConnectionsLoading,
    isError,
  } = useConnections(
    merchant.id,
    {connectorId: merchantConnectorId ? [merchantConnectorId] : undefined},
    appSpec !== null
  );

  const connectionsCounter = merchantConnections?.length ?? 0;

  const pageContent: JSX.Element = match({
    merchantConnections,
    isConnectionsLoading,
    isError,
    appSpec,
    merchantConnectorId,
  })
    .with({isConnectionsLoading: true}, () => {
      return <LoadingState />;
    })
    .with({isError: true}, () => <ErrorState />)
    .with(
      {merchantConnectorId: undefined},
      {merchantConnections: undefined},
      {merchantConnections: []},
      {appSpec: null},
      () => {
        return (
          <Fragment>
            <EmptyState title={t('titleEmpty')} description={t('descriptionEmpty')} />
            {showConnectionModal && (
              <EditConnectionModal
                onClose={() => setShowConnectionModal(false)}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                appId={appSpec!.id}
              />
            )}
          </Fragment>
        );
      }
    )
    .otherwise(() => {
      return (
        <div className="space-y-8">
          <div className="w-1/3">
            <KPICard
              icon={<Link size={20} />}
              text={t('connectionsCount', {count: connectionsCounter})}
              value={connectionsCounter}
            />
          </div>
          <div className="mt-10">
            <ConnectionsTable
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              merchantConnections={merchantConnections!}
              isConnectionsLoading={isConnectionsLoading}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              appSpec={appSpec!}
            />
          </div>
          {showConnectionModal && (
            <EditConnectionModal
              onClose={() => setShowConnectionModal(false)}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              appId={appSpec!.id}
            />
          )}
        </div>
      );
    });

  const headerConfig = {
    title: (
      <div className="flex items-center gap-x-3">
        {app?.logo}
        {appSpec?.name}
      </div>
    ),
    topRightElements: !hideCreateConnectionButton && (
      <Button key="addConnection" onClick={() => setShowConnectionModal(true)}>
        {t('addConnectionButton')}
      </Button>
    ),
  };

  return <AdminPage header={headerConfig}>{pageContent}</AdminPage>;
};

export default AppOverviewPage;
